import { useContext, useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  OutlinedInput,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker, DesktopTimePicker } from '@mui/x-date-pickers-pro';
import { wellzTheme as muiWellzTheme } from '@wellz-theme/mui';
import { infoSessionCancelledAtom } from 'atoms/inviteSession';
import { useAtomValue } from 'jotai';
import { DateTime } from 'luxon';
import { FormattedMessage, useIntl } from 'react-intl';
import { wellzTheme } from 'theme';

import { getPatients } from 'services/specialist';

import { timezone } from 'components/DrawerInvitationHistory/utils';
import { DrawerInviteScheduleContext } from 'components/DrawerInviteSchedule/context/DrawerInviteScheduleContext';
import { Patient } from 'pages/PatientList/types';

import { patientObject } from '../../constants';
import { debounced } from '../../utils';
import Tooltipicon from '../Tooltipicon';

import * as S from '../../styles';

const CreateInvite: React.FC = () => {
  const intl = useIntl();
  const {
    date,
    setDate,
    hour,
    setHour,
    patient,
    setPatient,
    error,
    setError,
    isTermsChecked,
    setIsTermsChecked,
  } = useContext(DrawerInviteScheduleContext);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isInvalidDate, setIsInvalidDate] = useState(false);
  const infoSessionCancelled = useAtomValue(infoSessionCancelledAtom);

  const today = DateTime.now().setZone(timezone);

  const getPatientList = async (patientName: string) => {
    try {
      const { results } = await getPatients(10, false, '', patientName);

      if (results) {
        const users: Patient[] = results.map(p => ({
          id: p.id,
          fullName: p.full_name,
          cognitoId: p.cognito_id,
          userId: p.user_id,
          patientAttention: p.patient_attention,
          totalCredits: p.total_credits,
          totalSessions: p.total_session,
          lastSession: p.last_session,
          createdAt: p.created_at,
          notifiesRenewal: p.notifies_renewal,
        }));

        setPatients(users);
      }
    } catch {
      setOpenSnackbar(true);
    }
  };

  const handleChangeSearch = (inputValue: string) => {
    if (inputValue && inputValue.length > 3) {
      debounced(() => getPatientList(inputValue));
    }
  };

  const scrollToSpecificHour = () => {
    setTimeout(() => {
      if (!hour) {
        const hoursList = document.querySelector('[aria-label="Select hours"]');

        const specificHourItem = document.querySelector(
          '[aria-label="7 hours"]',
        ) as HTMLDataListElement;

        hoursList?.scrollTo(0, specificHourItem.offsetTop);
      }
    }, 100);
  };

  const handleInputDateChange = (inputValue: Date | null) => {
    try {
      const currentDate = inputValue?.toISOString();

      if (currentDate) {
        const diff =
          DateTime.fromISO(currentDate.toString()).startOf('day') <
          today.startOf('day');

        if (!diff) setError({ isDateError: false, isTimeError: false });

        setIsInvalidDate(false);
        setDate(inputValue);
      }
    } catch (e) {
      setError({ isDateError: true, isTimeError: false });
      setIsInvalidDate(true);
    }
  };

  const minTime = useMemo(() => {
    if (!date || isInvalidDate) {
      return undefined;
    }

    const dateTimezoneFormated = DateTime.fromISO(date.toISOString(), {
      zone: 'utc',
    }).setZone(timezone);

    if (today.hasSame(dateTimezoneFormated, 'day')) {
      return new Date(today.toString());
    }

    return date;
  }, [date, isInvalidDate, today]);

  return (
    <>
      <S.Body className="createInvite">
        <div>
          <Typography
            variant="h5"
            style={{ marginBottom: muiWellzTheme.spacing(1) }}
          >
            <FormattedMessage id="CPPLATFORM.CPINVITECREATING.TITLE" />
          </Typography>

          <Typography
            style={{ marginBottom: muiWellzTheme.spacing(5) }}
            variant="body1"
            color={wellzTheme.colors.text.disabled}
          >
            <FormattedMessage id="CPPLATFORM.CPINVITECREATING.SUBTITLE" />
          </Typography>

          {infoSessionCancelled ? (
            <FormControl
              data-testid="patient-rescheduled"
              sx={{ width: '100%' }}
            >
              <InputLabel>
                <FormattedMessage id="CPPLATFORM.CPINVITECREATING.PATIENTTEXTFIELD" />
              </InputLabel>
              <OutlinedInput
                label={intl.formatMessage({
                  id: 'CPPLATFORM.CPINVITECREATING.PATIENTTEXTFIELD',
                })}
                size="medium"
                sx={{
                  width: '100%',
                  borderRadius: 2,
                }}
                value={infoSessionCancelled.fullName}
                disabled
                endAdornment={<Tooltipicon />}
              />
            </FormControl>
          ) : (
            <Autocomplete
              onChange={(_, current: Patient | null) =>
                setPatient(current || patientObject)
              }
              onInputChange={(_, current) => handleChangeSearch(current)}
              value={patient}
              getOptionLabel={option => option?.fullName}
              disablePortal
              options={patients}
              noOptionsText=""
              ListboxProps={{
                sx: {
                  background: 'white',
                },
              }}
              sx={{
                width: '100%',
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={intl.formatMessage({
                    id: 'CPPLATFORM.CPINVITECREATING.PATIENTTEXTFIELD',
                  })}
                />
              )}
            />
          )}

          <S.ContainerInputDate>
            <DesktopDatePicker
              disablePast
              onError={e => {
                if (e === 'invalidDate') {
                  setIsInvalidDate(true);
                }
                if (e === 'disablePast') {
                  setError({ ...error, isDateError: true });
                }
              }}
              slotProps={{
                popper: {
                  placement: 'bottom-end',
                },
                layout: {
                  sx: {
                    backgroundColor: 'white',
                  },
                },
                textField: {
                  helperText:
                    error.isDateError && date
                      ? intl.formatMessage({
                          id: 'CPPLATAFORM.CPNEWTIME.ERRORDATE',
                        })
                      : '',
                },
              }}
              sx={{
                width: '100%',
                height: 56,
              }}
              label={intl.formatMessage({
                id: 'CPPLATFORM.CPINVITECREATING.DATELABELTITLE',
              })}
              value={date}
              onChange={handleInputDateChange}
            />
          </S.ContainerInputDate>

          <S.ContainerInputHour>
            <DesktopTimePicker
              minTime={minTime}
              onError={e => {
                if (e === 'minTime') {
                  setError({ ...error, isTimeError: true });
                }
              }}
              label={intl.formatMessage({
                id: 'CPPLATFORM.CPINVITECREATING.TIMELABELTITLE',
              })}
              onOpen={scrollToSpecificHour}
              slotProps={{
                actionBar: {
                  actions: [],
                },
                popper: {
                  placement: 'bottom-end',
                  sx: {
                    '& .MuiList-root': {
                      scrollbarWidth: 'none',
                    },
                  },
                },
                layout: {
                  sx: {
                    backgroundColor: 'white',
                  },
                },
                textField: {
                  helperText:
                    error.isTimeError && hour
                      ? intl.formatMessage({
                          id: 'CPPLATAFORM.CPNEWTIME.ERRORTIME',
                        })
                      : '',
                },
              }}
              sx={{
                width: '100%',
                height: 56,
              }}
              value={hour}
              onChange={newValue => {
                setHour(newValue);
                setError({ ...error, isTimeError: false });
              }}
            />
          </S.ContainerInputHour>
        </div>

        <FormControlLabel
          style={{ marginLeft: 0 }}
          control={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <S.Checkbox
              data-testid="terms-checkbox"
              checked={isTermsChecked}
              onChange={event => setIsTermsChecked(event.target.checked)}
            />
          }
          label={intl.formatMessage({
            id: 'CPPLATFORM.CPINVITEORNEWTIME.EMERGENCYCHECKBOX',
          })}
        />
      </S.Body>

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => {
          setOpenSnackbar(false);
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          icon={<S.CustomWarningAmberIcon />}
          sx={{ width: '100%' }}
        >
          <FormattedMessage id="ENDUSER.DRAWER.CRITICAL.NOTIFICATION.WARNING" />
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateInvite;
